import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client'

import BackHeader from '../../components/BackHeader/BackHeader'
import Button from '../../components/Button/Button'
import Card from '../../components/Card/Card'
import Modal from '../../components/Modal/Modal'
import Notification from '../../components/Notification/Notification'
import Loading from '../../components/Loading/Loading'
import PullToRefresh from '../../components/PullToRefresh/PullToRefresh'

import { LANDSKAMP_GUESSES, LANDSKAMP_MATCHES } from '../../graphql/queries'
import { UPSERT_MY_LANDSKAMP_GUESS } from '../../graphql/mutations'

import './Landskamp.css'

function landskampGuessMatchesResult(match, guess) {
  if (!match || !guess) return false
  if (!Number.isInteger(match.homeScore) || !Number.isInteger(match.awayScore)) return false
  return guess.homeScore === match.homeScore && guess.awayScore === match.awayScore
}

function Landskamp() {
  const navigate = useNavigate()
  const DK_TEAM_NAME = 'Danmark'

  const user = useSelector((state) => state.mate.currentUser)
  const mateId = user?.id

  const [selectedMatchId, setSelectedMatchId] = useState(null)
  const [showGuesses, setShowGuesses] = useState(false)
  const [guessDialogOpen, setGuessDialogOpen] = useState(false)
  const [homeScore, setHomeScore] = useState('')
  const [awayScore, setAwayScore] = useState('')
  const [toast, setToast] = useState({ open: false, message: '', type: 'info' })
  const [refreshingGuesses, setRefreshingGuesses] = useState(false)
  const [refreshStatus, setRefreshStatus] = useState('idle') // idle | loading | success | error
  const refreshResetRef = useRef(null)
  const nextMatchRef = useRef(null)
  const matchesContainerRef = useRef(null)
  const [prefillFromMyGuess, setPrefillFromMyGuess] = useState(false)
  const [nowTick, setNowTick] = useState(Date.now())

  const { data: matchesData, loading: matchesLoading, error: matchesError, refetch } = useQuery(LANDSKAMP_MATCHES, {
    fetchPolicy: 'cache-and-network',
  })

  const fixtures = useMemo(() => {
    const fromDb = matchesData?.landskampMatches
    const list = Array.isArray(fromDb) ? fromDb : []
    return [...list].sort((a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime())
  }, [matchesData])

  const MATCH_DURATION_MS = 100 * 60 * 1000 // 100 min

  const { pastMatches, featuredMatch, upcomingMatches, displayOrder } = useMemo(() => {
    const now = nowTick
    const sorted = [...fixtures].sort((a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime())

    let nextIdx = null
    for (let i = 0; i < sorted.length; i++) {
      const kickoff = new Date(sorted[i].kickoff).getTime()
      if (kickoff + MATCH_DURATION_MS > now) {
        nextIdx = i
        break
      }
    }

    let past = []
    let next = null
    let upcoming = []

    if (nextIdx !== null) {
      past = sorted.slice(0, nextIdx)
      next = sorted[nextIdx]
      upcoming = sorted.slice(nextIdx + 1)
    } else {
      past = [...sorted]
    }

    const order = [...past, next, ...upcoming].filter(Boolean)
    return {
      pastMatches: past,
      featuredMatch: next,
      upcomingMatches: upcoming,
      displayOrder: order,
    }
  }, [fixtures, nowTick])

  useEffect(() => {
    const defaultId = featuredMatch?.id || displayOrder[0]?.id
    if (!defaultId) return
    if (!selectedMatchId) {
      setSelectedMatchId(defaultId)
      return
    }
    const stillExists = fixtures.some((m) => m.id === selectedMatchId)
    if (!stillExists) setSelectedMatchId(defaultId)
  }, [featuredMatch?.id, displayOrder, fixtures, selectedMatchId])

  const selectedMatch = useMemo(() => {
    return fixtures.find((m) => m.id === selectedMatchId) || null
  }, [fixtures, selectedMatchId])

  const {
    data: guessesData,
    loading: guessesLoading,
    error: guessesError,
    refetch: refetchGuesses,
  } = useQuery(LANDSKAMP_GUESSES, {
    variables: { matchId: selectedMatchId },
    skip: !selectedMatchId,
    fetchPolicy: 'cache-and-network',
  })

  const matchGuesses = useMemo(() => {
    const list = guessesData?.landskampGuesses
    return Array.isArray(list) ? list : []
  }, [guessesData])

  const myGuess = useMemo(() => {
    if (!mateId) return null
    return matchGuesses.find((g) => g.mateId === mateId) || null
  }, [matchGuesses, mateId])

  const [upsertMyGuess, { loading: savingGuess }] = useMutation(UPSERT_MY_LANDSKAMP_GUESS, {
    onCompleted: () => {
      setToast({ open: true, message: 'Gæt gemt!', type: 'success' })
      setGuessDialogOpen(false)
      refetchGuesses()
    },
    onError: (e) => {
      setToast({ open: true, message: e.message || 'Kunne ikke gemme gæt.', type: 'error' })
    },
  })

  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    if (!showGuesses || !selectedMatchId) return
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia('(max-width: 768px)')
    const apply = () => {
      document.body.style.overflow = mq.matches ? 'hidden' : ''
    }
    apply()
    mq.addEventListener('change', apply)
    return () => {
      mq.removeEventListener('change', apply)
      document.body.style.overflow = ''
    }
  }, [showGuesses, selectedMatchId])

  useEffect(() => {
    return () => {
      if (refreshResetRef.current) clearTimeout(refreshResetRef.current)
    }
  }, [])

  useEffect(() => {
    if (!featuredMatch?.id || !nextMatchRef.current || !matchesContainerRef.current) return
    nextMatchRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [featuredMatch?.id])

  const formatKickoff = (iso) => {
    const d = new Date(iso)
    return new Intl.DateTimeFormat('da-DK', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  }

  const isLocked = (kickoffIso) => {
    const kickoff = new Date(kickoffIso).getTime()
    return Date.now() >= kickoff
  }

  const formatCountdown = (kickoffIso) => {
    const kickoff = new Date(kickoffIso).getTime()
    const diff = kickoff - nowTick
    if (diff <= 0) return 'Starter nu'
    const totalSec = Math.floor(diff / 1000)
    const days = Math.floor(totalSec / 86400)
    const hours = Math.floor((totalSec % 86400) / 3600)
    const mins = Math.floor((totalSec % 3600) / 60)
    const secs = totalSec % 60

    if (days > 0) return `Starter om ${days}d ${hours}t ${mins}m`
    if (hours > 0) return `Starter om ${hours}t ${mins}m`
    if (mins > 0) return `Starter om ${mins}m ${secs}s`
    return `Starter om ${secs}s`
  }

  const hasFinalScore = (match) => {
    if (!match) return false
    return Number.isInteger(match.homeScore) && Number.isInteger(match.awayScore)
  }

  const getMatchStatus = (match) => {
    if (!match) return { label: '', showFootball: false }
    const kickoff = new Date(match.kickoff).getTime()
    if (Number.isNaN(kickoff)) return { label: 'Ukendt', showFootball: false }

    if (nowTick < kickoff) return { label: 'Ikke startet', showFootball: false }
    const elapsed = nowTick - kickoff
    if (hasFinalScore(match) || elapsed >= MATCH_DURATION_MS) return { label: 'Overstået', showFootball: false }
    return { label: 'I gang', showFootball: true }
  }

  const openGuessDialog = (match) => {
    setSelectedMatchId(match.id)
    setHomeScore('')
    setAwayScore('')
    setPrefillFromMyGuess(true)
    setGuessDialogOpen(true)
  }

  const closeGuessDialog = () => {
    setGuessDialogOpen(false)
    setPrefillFromMyGuess(false)
  }

  useEffect(() => {
    if (!guessDialogOpen) return
    if (!prefillFromMyGuess) return
    if (guessesLoading) return
    if (homeScore !== '' || awayScore !== '') {
      setPrefillFromMyGuess(false)
      return
    }
    if (myGuess) {
      setHomeScore(myGuess.homeScore ?? '')
      setAwayScore(myGuess.awayScore ?? '')
    }
    setPrefillFromMyGuess(false)
  }, [guessDialogOpen, prefillFromMyGuess, guessesLoading, myGuess, homeScore, awayScore])

  const closeToast = () => setToast((p) => ({ ...p, open: false }))

  const handleRefreshGuesses = async () => {
    if (!refetchGuesses) return
    if (refreshingGuesses) return
    if (refreshResetRef.current) clearTimeout(refreshResetRef.current)
    setRefreshStatus('loading')
    setRefreshingGuesses(true)
    try {
      await refetchGuesses()
      setRefreshStatus('success')
      refreshResetRef.current = setTimeout(() => setRefreshStatus('idle'), 1200)
    } catch (e) {
      setRefreshStatus('error')
      refreshResetRef.current = setTimeout(() => setRefreshStatus('idle'), 2000)
    } finally {
      setRefreshingGuesses(false)
    }
  }

  const clampScore = (value) => {
    if (value === '' || value === null || value === undefined) return ''
    const n = Number(value)
    if (Number.isNaN(n)) return ''
    return Math.max(0, Math.min(20, Math.trunc(n)))
  }

  const handleSaveGuess = () => {
    if (!selectedMatch || !mateId) return
    if (isLocked(selectedMatch.kickoff)) {
      setToast({ open: true, message: 'Kampen er startet, der kan ikke gættes mere på denne kamp. ', type: 'warning' })
      return
    }

    const nextHome = clampScore(homeScore)
    const nextAway = clampScore(awayScore)
    if (nextHome === '' || nextAway === '') {
      setToast({ open: true, message: 'Udfyld begge score-felter.', type: 'error' })
      return
    }

    upsertMyGuess({
      variables: {
        matchId: selectedMatch.id,
        homeScore: nextHome,
        awayScore: nextAway,
      },
    })
  }

  const handleMatchClick = (match) => {
    if (!match?.id) return
    if (selectedMatchId === match.id) {
      setShowGuesses((v) => !v)
      return
    }
    setSelectedMatchId(match.id)
    setShowGuesses(true)
  }

  const refreshLandskamp = async () => {
    await refetch()
    if (selectedMatchId) await refetchGuesses()
  }

  return (
    <PullToRefresh onRefresh={refreshLandskamp}>
    <div className="landskamp-page">
      <div className="landskamp-background"></div>

      <div className="landskamp-container">
        <BackHeader
          className="landskamp-bar"
          theme="landskamp"
          eyebrow="Svirr"
          title="Landskamp"
          onBack={() => navigate('/spil')}
          backLabel="Tilbage til spil"
        />

        {matchesError ? <p className="landskamp-muted">Kunne ikke hente kampe lige nu.</p> : null}

        <div className="landskamp-content-below-header">
        <Notification
          open={toast.open}
          onClose={closeToast}
          message={toast.message}
          type={toast.type}
          autoHideDuration={3500}
        />

        {matchesLoading && displayOrder.length === 0 ? <p className="landskamp-muted">Henter kampe...</p> : null}

        {displayOrder.length > 0 ? (
          <div
            className={`landskamp-matches-wrapper${showGuesses && selectedMatchId ? ' landskamp-matches-wrapper--mobile-fs-active' : ''}`}
            ref={matchesContainerRef}
          >
            <div className="landskamp-matches">
            {displayOrder.map((match, idx) => {
              const isFeatured = featuredMatch?.id && match.id === featuredMatch.id
              const isPast = pastMatches.some((m) => m.id === match.id)
              const isSelected = selectedMatchId === match.id
              const isExpanded = showGuesses && isSelected
              const kickoffTs = new Date(match.kickoff).getTime()
              const { label: statusLabel, showFootball } = getMatchStatus(match)

              return (
                <div
                  key={match.id}
                  ref={isFeatured ? nextMatchRef : null}
                  className={`landskamp-clickable ${isPast ? 'landskamp-match--past' : ''} ${isFeatured ? 'landskamp-match--next' : ''} ${isExpanded ? 'landskamp-match-fullscreen' : ''}`}
                  onClick={() => handleMatchClick(match)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleMatchClick(match)
                  }}
                >
                  {isExpanded ? (
                    <BackHeader
                      className="landskamp-mobile-fs-back"
                      theme="landskamp"
                      eyebrow="Landskamp"
                      title="Kampoversigt"
                      onBack={(event) => {
                        event.stopPropagation()
                        setShowGuesses(false)
                      }}
                      backLabel="Tilbage til kampoversigt"
                    />
                  ) : null}
                  <Card
                    padding={isFeatured ? 'medium' : 'small'}
                    className={`landskamp-next-card landskamp-match-card ${
                      isFeatured ? 'landskamp-match-card--featured' : 'landskamp-match-card--compact'
                    } ${isPast ? 'landskamp-match-card--past' : ''}`}
                    hover={false}
                  >
                    <div className="landskamp-card-top">
                      <div className="landskamp-kickoff">{formatKickoff(match.kickoff)}</div>

                      <div className="landskamp-card-chips">
                        {isFeatured ? <span className="landskamp-chip next">Næste kamp</span> : null}
                        <span className={`landskamp-chip ${match.homeTeam === DK_TEAM_NAME ? 'home' : 'away'}`}>
                          {match.homeTeam === DK_TEAM_NAME ? 'Hjemme' : 'Ude'}
                        </span>
                      </div>
                    </div>

                    <div className="landskamp-scoreboard">
                      <div>
                        <div className="landskamp-details-title">
                          {match.homeTeam} <span className="landskamp-vs">vs</span> {match.awayTeam}
                        </div>
                        <div className="landskamp-meta">
                          {match.competition} · {match.venue}
                          {match.city ? `, ${match.city}` : ''}
                        </div>
                        <div className="landskamp-statusline">
                          {nowTick < kickoffTs ? formatCountdown(match.kickoff) : `Status: ${statusLabel}`}
                        </div>
                      </div>

                      <div className="landskamp-livebox">
                        <span className={`landskamp-chip live ${showFootball ? 'landskamp-chip--live-icon' : ''}`}>
                          {showFootball ? (
                            <>
                              <span className="landskamp-football-icon" aria-hidden="true">⚽</span>
                              {statusLabel}
                            </>
                          ) : (
                            statusLabel
                          )}
                        </span>
                        <div className="landskamp-live-score">
                          {Number.isInteger(match.homeScore) ? match.homeScore : '-'}-{Number.isInteger(match.awayScore) ? match.awayScore : '-'}
                        </div>
                      </div>
                    </div>

                    {!isLocked(match.kickoff) && (
                      <div className="landskamp-actions" onClick={(e) => e.stopPropagation()}>
                        <Button
                          title={isSelected && myGuess ? 'Ret mit gæt' : 'Gæt resultat'}
                          onClick={() => openGuessDialog(match)}
                          color="secondary"
                        />
                      </div>
                    )}

                    {isExpanded && (
                      <div className="landskamp-guesses" onClick={(e) => e.stopPropagation()}>
                        <div className="landskamp-guesses__header">
                          <strong>Alle gæt på denne kamp</strong>
                          {nowTick < kickoffTs ? (
                            <Button
                              title={
                                refreshStatus === 'success' ? (
                                  <span className="landskamp-refresh-btn">
                                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                      <path
                                        d="M20 6 9 17l-5-5"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <span>Opdateret</span>
                                  </span>
                                ) : refreshStatus === 'error' ? (
                                  <span className="landskamp-refresh-btn">
                                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                      <path
                                        d="M12 9v4"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                      />
                                      <path
                                        d="M12 17h.01"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                      />
                                      <path
                                        d="M10.3 3.6 2.1 18.1a2 2 0 0 0 1.7 3h16.4a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0Z"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <span>Fejl</span>
                                  </span>
                                ) : refreshStatus === 'loading' ? (
                                  <Loading size="small" variant="refresh" inline text="Opdaterer..." />
                                ) : (
                                  <span className="landskamp-refresh-btn">
                                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                      <path
                                        d="M21 12a9 9 0 1 1-3.3-6.9"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <polyline
                                        points="21 3 21 9 15 9"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                    <span>Opdatér</span>
                                  </span>
                                )
                              }
                              variant="contained"
                              color={refreshStatus === 'error' ? 'danger' : 'primary'}
                              className="landskamp-refresh-button"
                              onClick={handleRefreshGuesses}
                              disabled={guessesLoading || refreshingGuesses}
                            />
                          ) : null}
                        </div>

                        {guessesError ? <div className="landskamp-muted">Kunne ikke hente gæt.</div> : null}
                        {guessesLoading ? <div className="landskamp-muted">Henter gæt...</div> : null}

                        {!guessesLoading && matchGuesses.length === 0 ? (
                          <div className="landskamp-muted">Der er ikke nogle gæt.</div>
                        ) : (
                          <ul className="landskamp-guess-list">
                            {matchGuesses.map((g) => {
                              const hitResult = landskampGuessMatchesResult(match, g)
                              return (
                                <li
                                  key={g.id}
                                  className={`landskamp-guess-item${hitResult ? ' landskamp-guess-item--correct' : ''}`}
                                  title={hitResult ? 'Ramte det rigtige resultat' : undefined}
                                >
                                  <span className="landskamp-guess-name">{g.mateName || 'Ukendt'}</span>
                                  <span className="landskamp-guess-score">
                                    {g.homeScore}-{g.awayScore}
                                    {hitResult ? (
                                      <span className="landskamp-guess-hit" aria-hidden="true">
                                        {' '}✓
                                      </span>
                                    ) : null}
                                  </span>
                                </li>
                              )
                            })}
                          </ul>
                        )}
                      </div>
                    )}
                  </Card>
                </div>
              )
            })}
            </div>
          </div>
        ) : (
          <p className="landskamp-muted">Ingen kampe.</p>
        )}
        </div>
      </div>

      <Modal
        isOpen={guessDialogOpen}
        onClose={closeGuessDialog}
        title="Gæt resultat"
        size="small"
        className="landskamp-modal"
      >
        {selectedMatch ? (
          <>
            <div className="landskamp-modal-subtitle">
              {selectedMatch.homeTeam} vs {selectedMatch.awayTeam}
            </div>
            <div className="landskamp-muted" style={{ marginBottom: 12 }}>
              {formatKickoff(selectedMatch.kickoff)} · {selectedMatch.competition}
            </div>

            <div className="landskamp-input-row">
              <label className="landskamp-field">
                <div className="landskamp-field-label">{selectedMatch.homeTeam}</div>
                <input
                  className="landskamp-input"
                  type="number"
                  min={0}
                  max={20}
                  value={homeScore}
                  onChange={(e) => setHomeScore(e.target.value)}
                />
              </label>
              <label className="landskamp-field">
                <div className="landskamp-field-label">{selectedMatch.awayTeam}</div>
                <input
                  className="landskamp-input"
                  type="number"
                  min={0}
                  max={20}
                  value={awayScore}
                  onChange={(e) => setAwayScore(e.target.value)}
                />
              </label>
            </div>

            <div className="landskamp-muted" style={{ marginTop: 10 }}>
              Score er begrænset til 0-20.
            </div>

            <div className="landskamp-modal-actions">
              <Button title="Annuller" onClick={closeGuessDialog} variant="outlined" color="danger"/>
              <Button title="Gem gæt" onClick={handleSaveGuess} color="confirmation" loading={savingGuess} loadingText="Gemmer..." />
            </div>
          </>
        ) : null}
      </Modal>
    </div>
    </PullToRefresh>
  )
}

export default Landskamp

